/**
 * Centralizes registration-target logic for events.
 *
 * Events that register attendees through an external form expose that form via
 * `webinar_form_registration_url` (capsules and webinars in practice). When that
 * URL is present, the website's own registration flow must not be used — the
 * register link points to the external form and the internal pages are guarded.
 */
export function useEventRegistration() {
  /**
   * Whether an event uses an external registration form.
   * @param {Object} event
   * @returns {boolean}
   */
  const usesExternalForm = (event) => !!event?.webinar_form_registration_url

  /**
   * The external registration form URL, or null.
   * @param {Object} event
   * @returns {string|null}
   */
  const externalFormUrl = (event) => event?.webinar_form_registration_url || null

  /**
   * Whether the current user has already submitted their registration for this event.
   * Relies on `event_registrations` being populated and filtered to the current user.
   * When that populate is absent (e.g. unauthenticated), returns false.
   * @param {Object} event
   * @returns {boolean}
   */
  const isRegistrationSubmitted = (event) => !!event?.event_registrations?.[0]?.submitted

  /**
   * Whether the current user has a started-but-not-submitted registration for this event (i.e. is
   * resuming). Also covers registrations created directly in SugarCRM by an administrator.
   *
   * **Warning:** Relies on `event_registrations` being populated and filtered to the current user.
   *
   * @param {Object} event
   * @returns {boolean}
   */
  const isRegistrationInProgress = (event) => {
    const registration = event?.event_registrations?.[0]
    return !!registration && !registration.submitted
  }

  /**
   * Build a descriptor for the registration link/button.
   *
   * A resuming user (registration in progress) goes straight to the form;
   * a first-timer goes through the confirmation step.
   *
   * @param {Object} event
   * @param {{ localePath: Function }} options
   * @returns {{ component: string, icon: string, external: boolean, props: Object }}
   */
  const getRegistrationLink = (event, { localePath } = {}) => {
    if (usesExternalForm(event)) {
      return {
        component: 'a',
        icon: 'arrow-up-right-from-square',
        external: true,
        props: {
          href: externalFormUrl(event),
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }
    }

    const routeName = isRegistrationInProgress(event) ? 'events-slug-register' : 'events-slug-register-confirm'

    return {
      component: 'NuxtLink',
      icon: 'arrow-right',
      external: false,
      props: {
        to: localePath({ name: routeName, params: { slug: event.slug } })
      }
    }
  }

  return {
    usesExternalForm,
    externalFormUrl,
    isRegistrationSubmitted,
    isRegistrationInProgress,
    getRegistrationLink
  }
}
